import { useMemo } from 'react';
import { useGraphData } from '../../hooks/useMeters';
import { buildGraphConfig } from '../../services/waterGraphConfig';
import { relativeRangeWithin } from '../../utils/financialYearWindow';
import { totalOf, breakdownOf } from '../../utils/comparisonSeries';
import { unitOf } from '../../utils/meterKpis';

/**
 * One side of a comparison.
 *
 * The window is resolved from the group's own financial year rather than from
 * today, which is the whole reason a year-against-year comparison works at
 * all: "last 30 days" against 2025-2026 has to mean the last thirty days that
 * year could report in, not thirty days that fall outside it and match
 * nothing. Each group therefore resolves its range separately even though both
 * read the same range name.
 *
 * Two requests go out per group. The first is the KPI's total; the second is
 * the same KPI split across the meters reporting it, which is the breakdown
 * every meter KPI has — Borewell divides into the borewells, and the question
 * "which one drove the change?" is the one a total always raises next.
 */

/** Field the breakdown request splits on. */
export const BREAKDOWN_FIELD = 'meter_id';

/**
 * Reads the rows out of a widget response, whichever shape it arrives in.
 * @param {object} payload - Response from the graph endpoint.
 * @returns {Array<object>} The rows, empty when there are none.
 */
const rowsOf = (payload) => {
  const rows = payload?.data?.data ?? payload?.data ?? [];
  return Array.isArray(rows) ? rows : [];
};

/**
 * Fetches one group's figures.
 *
 * @param {object} options - What this group is scoped to.
 * @param {object} options.kpi - A descriptor from `kpiCatalogue`.
 * @param {Array<string>} options.meterIds - Narrow the group to these meters.
 *   Several meters can share one KPI — Shibra has five — and naming only the
 *   KPI reads their sum, which cannot answer whether an inlet tracks an outlet.
 * @param {string} options.financialYear - Year this group reads.
 * @param {string|number} options.locationId - Location this group reads.
 * @param {string|number} options.subLocationId - Shared sub-location filter.
 * @param {string} options.timeRange - Range name, shared by both groups.
 * @param {string} options.aggregation - Bucket size, shared by both groups.
 * @param {boolean} options.enabled - Whether the group is ready to fetch.
 * @returns {object} The group's rows, total, breakdown, unit and request state.
 */
export const useComparisonSeries = ({
  kpi,
  meterIds,
  financialYear,
  locationId,
  subLocationId,
  timeRange,
  aggregation,
  enabled = true,
}) => {
  const range = useMemo(
    () => (financialYear ? relativeRangeWithin(timeRange, financialYear) : null),
    [timeRange, financialYear]
  );

  /** Everything both requests share, held stable so neither refires on a
      render that changed none of it. */
  const scope = useMemo(
    () =>
      kpi
        ? {
            financialYear,
            window: range,
            locationId,
            subLocationId,
            aggregation,
            title: kpi.title,
            module: kpi.module,
            category: kpi.category,
            subCategory: kpi.subCategory,
            kpi: kpi.title,
            ...(meterIds?.length ? { meterId: meterIds } : {}),
          }
        : null,
    [kpi, meterIds, financialYear, range, locationId, subLocationId, aggregation]
  );

  const totalConfig = useMemo(
    () => (scope ? buildGraphConfig(scope) : null),
    [scope]
  );

  const breakdownConfig = useMemo(
    () => (scope ? buildGraphConfig({ ...scope, stackByField: BREAKDOWN_FIELD }) : null),
    [scope]
  );

  const total = useGraphData(totalConfig, { enabled: enabled && !!totalConfig });
  const split = useGraphData(breakdownConfig, { enabled: enabled && !!breakdownConfig });

  const rows = useMemo(() => rowsOf(total.data), [total.data]);

  const breakdown = useMemo(
    () => breakdownOf(rowsOf(split.data), BREAKDOWN_FIELD),
    [split.data]
  );

  return {
    rows,
    total: totalOf(rows),
    breakdown,
    unit: unitOf(rows),
    range,
    isLoading: total.isLoading || split.isLoading,
    error: total.error || split.error,
  };
};
